/**
 * Les sorties d'argent du relevé bancaire (camt.053), reconnues.
 *
 * <p>Le relevé s'affichait sans rien relier : le patron qui payait sa
 * fiduciaire par bulletin de versement devait encore cocher le mois à la
 * main, et un paiement à un fournisseur ne disait pas s'il était déjà compté.
 * Chaque sortie est ici rapprochée de ce que SwissTech connaît déjà.
 */

/** Jour « AAAA-MM-JJ » d'une date ISO ou suisse (jj.mm.aaaa). */
export function jourIso(d) {
  const s = String(d || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  return m ? m[3] + '-' + m[2].padStart(2, '0') + '-' + m[1].padStart(2, '0') : '';
}

// Comparés sans accents : « Sàrl » s'y lit « sarl ».
const MOTS_VIDES = new Set(['sarl', 'gmbh', 'fiduciaire', 'treuhand', 'societe']);

/** Mots distinctifs d'un nom : « Fiduciaire Genève SA » → ['geneve']. */
function motsDuNom(nom) {
  return String(nom || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((m) => m.length >= 4 && !MOTS_VIDES.has(m));
}

const sansAccents = (t) =>
  String(t || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/**
 * Période de facturation du comptable dans laquelle tombe un jour.
 *
 * @param {string} freq `mensuel`, `trimestriel`, `semestriel` ou `annuel`
 * @param {string} jour « AAAA-MM-JJ »
 * @returns {{annee: number, periode: string}} exercice et clé de période
 */
export function periodeDuComptable(freq, jour) {
  const annee = +jour.slice(0, 4);
  const mois = +jour.slice(5, 7);
  if (freq === 'trimestriel') return { annee, periode: 'T' + Math.ceil(mois / 3) };
  if (freq === 'semestriel') return { annee, periode: mois <= 6 ? 'S1' : 'S2' };
  if (freq === 'annuel') return { annee, periode: 'AN' };
  return { annee, periode: String(mois).padStart(2, '0') };
}

const memeMontant = (a, b) => Math.abs((+a || 0) - (+b || 0)) < 0.005;

const JOUR_MS = 24 * 3600 * 1000;
const DELAI_PAIEMENT_JOURS = 120;

/**
 * La fenêtre d'une dépense : payée jusqu'à sept jours avant sa date (le ticket
 * saisi plus tard, daté du jour de la saisie) et dix jours après (le débit de
 * la carte). Une facture à payer par la banque (IBAN noté, ou payée « par la
 * banque ») peut l'être jusqu'à quatre mois après. Un abonnement du même
 * montant, le mois d'avant, n'est pas la même dépense.
 */
const AVANT_JOURS = 7;
const APRES_JOURS = 10;
/** À trois jours près, le montant seul suffit ; au-delà, le nom doit y être. */
const MEME_JOUR_JOURS = 3;

const aPayerParLaBanque = (x) => !!(x.iban || x.paiement === 'banque');

// Les mots de la banque, des formes de société et des villes : présents dans
// tant de libellés qu'ils ne nomment personne.
const MOTS_COMMUNS = new Set([
  'achat',
  'paiement',
  'mastercard',
  'maestro',
  'visa',
  'debit',
  'debitkarte',
  'carte',
  'twint',
  'ordre',
  'banking',
  'virement',
  'versement',
  'prelevement',
  'recouvrement',
  'retrait',
  'bancomat',
  'postomat',
  'einkauf',
  'zahlung',
  'credit',
  'facture',
  'rechnung',
  'suisse',
  'schweiz',
  'svizzera',
  'geneve',
  'genf',
  'lausanne',
  'zurich',
  'bern',
  'berne',
  'basel',
  'bale',
  'lugano',
  'fribourg',
  'neuchatel',
  'sion',
  'carouge',
  'meyrin',
  'lancy',
  'vernier',
  'onex',
]);

/** Les mots qui nomment une dépense : « Hornbach Genève » → ['hornbach']. */
const motsDeLaDepense = (x) =>
  motsDuNom([x.fournisseur, x.libelle, x.lib].filter(Boolean).join(' ')).filter(
    (m) => !MOTS_COMMUNS.has(m),
  );

/** Le montant en devise d'un libellé de carte (« EUR 45.00 » ou « 45.00 EUR »). */
export function montantEnDevise(label) {
  const s = String(label || '');
  const m =
    s.match(/\b(EUR|USD|GBP)\s?(\d{1,3}(?:['’ ]?\d{3})*[.,]\d{2})\b/) ||
    s.match(/\b(\d{1,3}(?:['’ ]?\d{3})*[.,]\d{2})\s?(EUR|USD|GBP)\b/);
  if (!m) return null;
  const [devise, chiffres] = /\d/.test(m[1]) ? [m[2], m[1]] : [m[1], m[2]];
  const montant = Number(chiffres.replace(/['’ ]/g, '').replace(',', '.'));
  return Number.isFinite(montant) ? { devise, montant } : null;
}

/**
 * Ce qu'une dépense est à une sortie : `null` si elle ne peut pas l'être,
 * sinon l'écart en jours, le montant en francs ou en devise, et son nom lu
 * dans le libellé ou non. Une dépense payée en espèces n'est jamais sur le
 * relevé.
 */
function rapport(e, t, jour, libelle) {
  const x = e.x;
  if (x.paiement === 'especes') return null;
  const date = Date.parse(jourIso(x.date));
  if (!jour || !Number.isFinite(date)) return null;
  const ecart = (Date.parse(jour) - date) / JOUR_MS;
  const apres = aPayerParLaBanque(x) ? DELAI_PAIEMENT_JOURS : APRES_JOURS;
  if (ecart < -AVANT_JOURS || ecart > apres) return null;
  const enFrancs = memeMontant(x.montant, t.montant);
  const devise = montantEnDevise(t.label);
  if (!enFrancs && !(devise && memeMontant(x.montant, devise.montant))) return null;
  const nommee = motsDeLaDepense(x).some((m) => libelle.includes(m));
  // Au même jour, à trois jours près, le montant en francs suffit ; au-delà,
  // ou en devise, le nom de la dépense doit être dans le libellé.
  const sure = nommee || (enFrancs && Math.abs(ecart) <= MEME_JOUR_JOURS);
  return { ecart, nommee, sure, memeJour: enFrancs && Math.abs(ecart) < 0.5 };
}

/**
 * La dépense payée par une sortie : même montant, à trois jours près ; plus
 * loin (jusqu'à dix jours après, ou quatre mois pour une facture à payer par
 * la banque), son nom doit être dans le libellé. Plusieurs : celle dont le
 * nom est dans le libellé, puis celle du jour même ; sinon l'une d'elles, sans
 * deviner (`incertaines`). Une dépense du même montant que rien n'assure est
 * seulement proposée (`proposees`) : la lier à tort datait payée une autre
 * dépense (l'abonnement du mois d'avant, le plein d'une autre station).
 */
function depensePayee(fraisLibres, t, jour, libelle, reservee) {
  const vues = fraisLibres
    .map((e) => ({ e, r: !e.prise && !reservee(e) ? rapport(e, t, jour, libelle) : null }))
    .filter((v) => v.r);
  const sures = vues.filter((v) => v.r.sure);
  if (!sures.length) return vues.length ? { proposees: vues.map((v) => v.e) } : null;
  if (sures.length === 1) return sures[0].e;
  const nommees = sures.filter((v) => v.r.nommee);
  if (nommees.length === 1) return nommees[0].e;
  const restantes = nommees.length ? nommees : sures;
  const memeJour = restantes.filter((v) => v.r.memeJour);
  if (memeJour.length >= 1 && (memeJour.length === 1 || !nommees.length)) return memeJour[0].e;
  // Plusieurs dépenses du même montant, que rien ne départage : la sortie est
  // l'une d'elles, sans qu'on devine laquelle. Laissée sans lien, elle était
  // proposée en dépense — comptée deux fois.
  return { incertaines: restantes.map((v) => v.e) };
}

/**
 * La pièce payée par une sortie : même montant, datée au plus tard le jour
 * du paiement et payée dans les quatre mois ; une pièce encore impayée
 * d'abord ; plusieurs, le nom du fournisseur dans le libellé départage.
 */
function piecePayee(libres, montantDe, dateDe, nomDe, t, jour, libelle) {
  if (!jour) return null;
  const paye = Date.parse(jour);
  const candidates = libres.filter((e) => {
    if (e.prise || !memeMontant(montantDe(e), t.montant)) return false;
    const date = Date.parse(jourIso(dateDe(e)));
    if (!Number.isFinite(date)) return true;
    const ecart = (paye - date) / JOUR_MS;
    return ecart >= -3 && ecart <= DELAI_PAIEMENT_JOURS;
  });
  if (candidates.length <= 1) return candidates[0] || null;
  const nommees = candidates.filter((e) => motsDuNom(nomDe(e)).some((m) => libelle.includes(m)));
  if (nommees.length === 1) return nommees[0];
  const restantes = nommees.length ? nommees : candidates;
  const impayees = restantes.filter((e) => !e.payeLe);
  if (impayees.length === 1) return impayees[0];
  // Encore plusieurs : l'une d'elles, sans deviner laquelle.
  return { incertaines: impayees.length ? impayees : restantes };
}

/**
 * Une sortie qui paie l'une de plusieurs pièces du même montant : déjà
 * comptée, mais aucune n'est datée payée à sa place.
 */
function incertaine(genre, liste, nomDe) {
  const noms = [...new Set(liste.map(nomDe).filter(Boolean))];
  return {
    genre,
    incertain: true,
    libelle: noms.join(' / '),
    fournisseur: noms.join(' / '),
    id: null,
    payeLe: '',
  };
}

/**
 * Rapproche chaque sortie du relevé.
 *
 * @param {object[]} mouvements lignes du relevé ({date, montant, sens, label, ref})
 * @param {object} connu ce que SwissTech connaît déjà
 * @param {object} [connu.comptable] fiche du comptable ({nom, montant, freq})
 * @param {object[]} [connu.chantiers] chantiers, avec leurs factures fournisseurs
 * @param {object[]} [connu.frais] dépenses saisies
 * @param {object[]} [connu.achats] factures de fournisseurs et de sous-traitants
 *   jointes aux offres et factures ({piece: {id, montant, fournisseur, date, payeLe}})
 * @returns {Array<object|null>} pour chaque mouvement : `null` (entrée ou
 *   inconnu), `{genre: 'comptable', annee, periode}`, `{genre: 'fournisseur',
 *   fournisseur, chantier, chantierId, id, payeLe}`, `{genre: 'soustraitant',
 *   fournisseur, chantier, chantierId, id, payeLe}`, `{genre: 'piece',
 *   fournisseur, id, payeLe}`, `{genre: 'depense', libelle, id, montant, date,
 *   payeLe}`, une dépense seulement proposée (`{genre: 'depense', propose: true,
 *   libelle, date, id: null}`) ou incertaine (`incertain: true`)
 */
export function rapprocheSorties(mouvements, { comptable, chantiers, frais, achats } = {}) {
  const motsComptable = motsDuNom(comptable?.nom);
  const facturesLibres = (chantiers || []).flatMap((ch) =>
    (ch.factures || []).map((f) => ({ f, ch, prise: false, payeLe: f.payeLe })),
  );
  const sousTraitantsLibres = (chantiers || []).flatMap((ch) =>
    (ch.sousTraitants || [])
      .filter((t) => +t.prixConvenu > 0)
      .map((t) => ({ t, ch, prise: false, payeLe: t.payeLe })),
  );
  const piecesLibres = (achats || [])
    .map((a) => a?.piece || a)
    .filter((p) => p && +p.montant > 0)
    .map((p) => ({ p, prise: false, payeLe: p.payeLe }));
  const fraisLibres = (frais || []).map((x) => ({ x, prise: false, pour: -1 }));
  // Une dépense nommée par une seule sortie lui est gardée : le même jour, du
  // même montant, une autre sortie (un parking, puis la Coop) la prenait.
  (mouvements || []).forEach((t, i) => {
    if (t.sens !== 'DBIT') return;
    const jour = jourIso(t.date);
    const libelle = sansAccents(t.label);
    const nommees = fraisLibres.filter((e) => {
      if (e.pour >= 0) return false;
      const r = rapport(e, t, jour, libelle);
      return r && r.nommee;
    });
    if (nommees.length === 1) nommees[0].pour = i;
  });
  return (mouvements || []).map((t, i) => {
    if (t.sens !== 'DBIT') return null;
    const jour = jourIso(t.date);
    const libelle = sansAccents(t.label);
    // Le comptable : tous les mots de son nom dans le libellé, ou son
    // montant exact avec une partie de son nom. Un montant seul, ou un mot
    // seul, reconnaissait d'autres paiements.
    const motsTrouves = motsComptable.filter((m) => libelle.includes(m)).length;
    if (
      comptable &&
      jour &&
      motsComptable.length > 0 &&
      (motsTrouves === motsComptable.length ||
        (motsTrouves > 0 && +comptable.montant > 0 && memeMontant(t.montant, comptable.montant)))
    ) {
      return { genre: 'comptable', ...periodeDuComptable(comptable.freq, jour) };
    }
    // Une dépense déjà saisie : même montant, à trois jours près ; plus loin
    // (dix jours, ou quatre mois pour une facture à payer par la banque), son
    // nom dans le libellé. Sans cela, le relevé proposait de l'ajouter en
    // dépense : comptée deux fois.
    const d = depensePayee(fraisLibres, t, jour, libelle, (e) => e.pour >= 0 && e.pour !== i);
    if (d?.incertaines) return incertaine('depense', d.incertaines, (e) => e.x.libelle || e.x.lib);
    let proposee = null;
    if (d?.proposees) {
      const noms = [...new Set(d.proposees.map((e) => e.x.libelle || e.x.lib).filter(Boolean))];
      proposee = {
        genre: 'depense',
        propose: true,
        libelle: noms.join(' / '),
        date: d.proposees[0].x.date || '',
        id: null,
        payeLe: '',
      };
    } else if (d) {
      d.prise = true;
      return {
        genre: 'depense',
        libelle: d.x.libelle || d.x.lib || '',
        id: d.x.id,
        montant: +d.x.montant || 0,
        date: d.x.date || '',
        payeLe: d.x.payeLe || '',
      };
    }
    // Une facture fournisseur d'un chantier : même montant TTC, la date et le
    // nom départageant.
    const f = piecePayee(
      facturesLibres,
      (e) => e.f.montant,
      (e) => e.f.date,
      (e) => e.f.fournisseur,
      t,
      jour,
      libelle,
    );
    if (f?.incertaines) return incertaine('fournisseur', f.incertaines, (e) => e.f.fournisseur);
    if (f) {
      f.prise = true;
      return {
        genre: 'fournisseur',
        fournisseur: f.f.fournisseur || '',
        chantier: f.ch.nom || '',
        chantierId: f.ch.id,
        id: f.f.id,
        payeLe: f.f.payeLe || '',
      };
    }
    // Un sous-traitant d'un chantier : son prix convenu.
    const st = piecePayee(
      sousTraitantsLibres,
      (e) => e.t.prixConvenu,
      (e) => e.ch.debut,
      (e) => e.t.entreprise,
      t,
      jour,
      libelle,
    );
    if (st?.incertaines) return incertaine('soustraitant', st.incertaines, (e) => e.t.entreprise);
    if (st) {
      st.prise = true;
      return {
        genre: 'soustraitant',
        fournisseur: st.t.entreprise || '',
        chantier: st.ch.nom || '',
        chantierId: st.ch.id,
        id: st.t.id,
        payeLe: st.t.payeLe || '',
      };
    }
    // Le matériel ou la sous-traitance scannés sur une offre ou une facture.
    const p = piecePayee(
      piecesLibres,
      (e) => e.p.montant,
      (e) => e.p.date,
      (e) => e.p.fournisseur || e.p.libelle,
      t,
      jour,
      libelle,
    );
    if (p?.incertaines) {
      return incertaine('piece', p.incertaines, (e) => e.p.fournisseur || e.p.libelle);
    }
    if (p) {
      p.prise = true;
      return {
        genre: 'piece',
        fournisseur: p.p.fournisseur || p.p.libelle || '',
        id: p.p.id,
        payeLe: p.p.payeLe ? jourSuisse(jourIso(p.p.payeLe)) : '',
      };
    }
    // Rien de sûr : la dépense du même montant est proposée, jamais liée.
    return proposee;
  });
}

/** « 2026-03-05 » écrit « 05.03.2026 ». */
const jourSuisse = (iso) => (iso ? iso.split('-').reverse().join('.') : '');

/**
 * Les paiements que le relevé date : chaque dépense, facture de fournisseur,
 * sous-traitant ou pièce jointe reconnue y prend le jour de sa sortie, payée
 * par la banque. Une date déjà notée n'est jamais remplacée : si elle diffère
 * de plus de trois jours de celle de la banque, elle est signalée à vérifier.
 *
 * @param {object[]} mouvements lignes du relevé
 * @param {Array<object|null>} liens rapprochements de {@link rapprocheSorties}
 * @returns {{aDater: object[], aVerifier: object[]}} les liens à dater
 *   (`{...lien, jour}`, jour « JJ.MM.AAAA ») et ceux dont la date diffère
 */
export function paiementsDuReleve(mouvements, liens) {
  const aDater = [];
  const aVerifier = [];
  (liens || []).forEach((l, i) => {
    if (
      !l ||
      l.incertain ||
      l.propose ||
      !['depense', 'fournisseur', 'soustraitant', 'piece'].includes(l.genre)
    )
      return;
    const iso = jourIso(mouvements[i]?.date);
    if (!iso) return;
    const jour = jourSuisse(iso);
    if (!l.payeLe) {
      aDater.push({ ...l, jour });
      return;
    }
    const ecart = Math.abs(Date.parse(jourIso(l.payeLe)) - Date.parse(iso)) / JOUR_MS;
    if (Number.isFinite(ecart) && ecart > 3) aVerifier.push({ ...l, jour });
  });
  return { aDater, aVerifier };
}

/** Jours d'écart admis entre un ticket et la même dépense notée depuis la banque. */
const ECART_TICKET_JOURS = 7;

/**
 * La dépense déjà notée sans justificatif — venue du relevé bancaire — que
 * le ticket scanné paie : même montant, à quelques jours près.
 *
 * @param {object[]} frais dépenses
 * @param {object} f la dépense en cours de saisie
 * @returns {object|undefined} la dépense déjà notée
 */
export function depenseSansPiece(frais, f) {
  const jour = (d) => Date.parse(jourIso(d));
  const date = jour(f.date);
  return (frais || []).find(
    (y) =>
      !y.piece &&
      !y.originalId &&
      Math.abs((+y.montant || 0) - (+f.montant || 0)) < 0.005 &&
      Number.isFinite(date) &&
      Math.abs(jour(y.date) - date) <= ECART_TICKET_JOURS * 24 * 3600 * 1000,
  );
}
